// src/components/merchant/PlanQuotaBanner.js
// Consommation du pack + invitation à monter en gamme
// Maxula — AbodyStudio Limited
//
// Les limites étaient déjà appliquées côté serveur (blocage à 50 annonces sur
// Bronze), mais rien ne prévenait AVANT le blocage. Un refus qui arrive sans
// prévenir se lit comme une panne, pas comme une invitation à changer de pack.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiAlertTriangle, FiArrowUpRight, FiZap } from 'react-icons/fi';
import { merchantAPI } from '../../services/api';

const fmt = (v) => (Number(v) || 0).toLocaleString('fr-FR');

/**
 * @param {boolean} compact  n'affiche rien tant que le seuil d'alerte n'est pas
 *                           atteint — pour les écrans où la barre serait du bruit.
 */
const PlanQuotaBanner = ({ compact = false }) => {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await merchantAPI.planUsage();
        if (alive) setUsage(res?.data?.data || null);
      } catch (e) {
        // Information de confort : son absence ne doit pas gêner l'écran.
        if (alive) setUsage(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!usage) return null;
  // Pack sans limite de deals : une barre « 12 / illimité » n'apprend rien.
  if (usage.maxDeals == null) return null;
  if (compact && !usage.nearLimit && !usage.atLimit) return null;

  const { usedDeals, maxDeals, percentDeals, atLimit, nearLimit, nextPlan, planName } = usage;

  const tone = atLimit
    ? { box: 'border-red-200 bg-red-50', bar: 'bg-red-500', text: 'text-red-800' }
    : nearLimit
      ? { box: 'border-amber-200 bg-amber-50', bar: 'bg-amber-500', text: 'text-amber-800' }
      : { box: 'border-gray-200 bg-white', bar: 'bg-primary-600', text: 'text-gray-700' };

  return (
    <div className={`rounded-xl border p-4 mb-5 ${tone.box}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className={`text-sm font-semibold flex items-center gap-2 ${tone.text}`}>
          {(atLimit || nearLimit) && <FiAlertTriangle size={15} />}
          Pack {planName} — {fmt(usedDeals)} / {fmt(maxDeals)} annonces
        </div>
        <div className="text-xs text-gray-500">
          Commission {usage.commissionRate}%
          {usage.maxPhotos ? ` · ${usage.maxPhotos} photos par annonce` : ''}
        </div>
      </div>

      <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
        <div className={`h-full ${tone.bar}`} style={{ width: `${percentDeals}%` }} />
      </div>

      {atLimit && (
        <p className={`text-sm mt-2 ${tone.text}`}>
          Vous avez atteint la limite de votre pack. Pour publier une nouvelle
          annonce, supprimez-en une ancienne ou passez au pack supérieur.
        </p>
      )}
      {nearLimit && !atLimit && (
        <p className={`text-sm mt-2 ${tone.text}`}>
          Il vous reste <b>{fmt(usage.remainingDeals)}</b> annonce
          {usage.remainingDeals > 1 ? 's' : ''} sur ce pack.
        </p>
      )}

      {/* Le bouton ne s'affiche que s'il existe VRAIMENT un pack au-dessus.
          Proposer une montée en gamme depuis le pack le plus élevé serait une
          promesse sans suite. */}
      {(atLimit || nearLimit) && nextPlan && (
        <Link
          to="/merchant/subscription"
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700"
        >
          <FiZap size={14} />
          Passer à {nextPlan.name} — {fmt(nextPlan.priceMonthly)} MAD/mois
          {nextPlan.maxDeals > 0 ? ` · ${fmt(nextPlan.maxDeals)} annonces` : ' · annonces illimitées'}
          <FiArrowUpRight size={14} />
        </Link>
      )}
    </div>
  );
};

export default PlanQuotaBanner;
